import React, {
  Context,
  JSX,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useLocation } from 'react-router-dom';

import ApiError from '~/types/ApiError';

import offerService, { OfferResponse } from '../../../services/cruises/OfferService';

type Offer = OfferResponse['result'];

interface CruiseOfferByIdData {
  offer: Offer;
  loadError: string | null;
  showInvalidOfferDetails: boolean;
}

interface CruiseOfferByIdProviderProps {
  children?: ReactNode;
}

export const CruiseOfferByIdContext: Context<CruiseOfferByIdData> = createContext({} as CruiseOfferByIdData);

function isOfferNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function CruiseOfferByIdProvider({ children }: CruiseOfferByIdProviderProps): JSX.Element {
  const { pathname, search } = useLocation();
  const [offer, setOffer] = useState<Offer>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showInvalidOfferDetails, setShowInvalidOfferDetails] = useState(false);
  const offerId = useMemo(() => pathname.split('/').pop(), [pathname]);

  const allowInvalidOfferFromUrl = useMemo(() => {
    const params = new URLSearchParams(search);
    return !!params.get('allowInvalidOffer');
  }, [search]);

  const loadOfferById = useCallback(
    async (id: string) => {
      setOffer(null);
      setLoadError(null);
      setShowInvalidOfferDetails(false);

      try {
        const { result } = await offerService.getById(id, {
          allowInvalidOffer: allowInvalidOfferFromUrl,
        });
        setOffer(result);
        setShowInvalidOfferDetails(allowInvalidOfferFromUrl);
        return;
      } catch (error: unknown) {
        if (!allowInvalidOfferFromUrl && isOfferNotFoundError(error)) {
          try {
            const { result } = await offerService.getById(id, {
              allowInvalidOffer: true,
            });
            setOffer(result);
            setShowInvalidOfferDetails(true);
            return;
          } catch (retryError: unknown) {
            setLoadError(retryError instanceof Error ? retryError.message : 'Failed to load cruise offer.');
            return;
          }
        }

        setLoadError(error instanceof Error ? error.message : 'Failed to load cruise offer.');
      }
    },
    [allowInvalidOfferFromUrl],
  );

  useEffect(() => {
    if (offerId) {
      void loadOfferById(offerId);
    }
  }, [offerId, loadOfferById]);

  return (
    <CruiseOfferByIdContext.Provider value={{ offer, loadError, showInvalidOfferDetails }}>
      {children}
    </CruiseOfferByIdContext.Provider>
  );
}

export const useCruiseOfferById = (): CruiseOfferByIdData => useContext(CruiseOfferByIdContext);
